import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import {
  MenuItem,
  Modal,
  FormHelperText,
  Select,
  TextField,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import ActionButton from '../../../components/ActionButton';

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(2),
    textAlign: 'center',
    backgroundColor: 'white',
    color: 'black',
    padding: theme.spacing(2),
    boxShadow: theme.shadows[6],
    borderRadius: '6px',
    overflowY: 'auto',
    width: '90%',
    maxWidth: '750px',
    maxHeight: '90vh',
    boxSizing: 'border-box',
  },
  modalTitle: {
    fontSize: 20,
    alignSelf: 'center',
    margin: theme.spacing(1, 0),
    flexShrink: 0,
  },
  selectWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    width: '100%',
    padding: theme.spacing(0, 2),
    boxSizing: 'border-box',
  },
  selectField: {
    width: '100%',
    maxWidth: 400,
    [theme.breakpoints.down('xs')]: {
      maxWidth: '100%',
    },
  },
  select: {
    width: '100%',
  },
  selectPlaceholder: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  inputWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  inputDescription: {
    // The field is taller than its input because of the reserved helper row,
    // so centring on the field would drop the label below the input. Match the
    // small outlined input's own height instead.
    lineHeight: '40px',
  },
  nameField: {
    // Fixed so the helper text wraps beneath the field instead of widening it,
    // the way NuovaCategoriaControlloModal constrains its own name field.
    width: 220,
  },
  fieldHelper: {
    // Two rows kept clear whether or not a message is showing, so neither the
    // field nor the buttons move when one appears.
    minHeight: 40,
    marginLeft: 0,
    marginRight: 0,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1, 0, 0),
    flexShrink: 0,
  },
  feedbackArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    width: '100%',
    minHeight: 72,
    maxHeight: 120,
    overflowY: 'auto',
    boxSizing: 'border-box',
  },
  warningText: {
    color: theme.palette.error.main,
    fontWeight: 600,
    width: '100%',
    maxWidth: 600,
    textAlign: 'center',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    margin: 0,
  },
  loadingWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    width: '100%',
    maxWidth: 600,
  },
}));

const EMPTY_NAME_MESSAGE = 'Inserire un nome per la tipologia.';
const EMPTY_SELECTION_MESSAGE = 'Selezionare una tipologia da rimuovere.';
const UNNAMED_TIPOLOGIA_LABEL = '(senza nome)';

// A tipologia saved before the name was validated reads as a blank row wherever
// it is listed, and the remove modal is the only place it can be deleted from:
// label it so it stays visible and pickable.
export const labelTipologia = (nome) =>
  (nome ?? '').trim() || UNNAMED_TIPOLOGIA_LABEL;

const TipologiaStrumentazioneModal = ({
  description,
  inputValue,
  onClose,
  onInputChange,
  onSave,
  open,
  title,
  selectOption = [],
  isSelect = false,
  warningMessage = '',
  loading = false,
  selectPlaceholder = 'Seleziona tipologia',
}) => {
  const classes = useStyles();
  const [validationError, setValidationError] = useState('');

  const handleInputChange = (event) => {
    setValidationError('');
    onInputChange(event);
  };

  const handleSave = () => {
    if (isSelect) {
      if (!inputValue) {
        setValidationError(EMPTY_SELECTION_MESSAGE);
        return;
      }
      onSave(inputValue);
      return;
    }

    const nome = String(inputValue).trim();
    if (!nome) {
      setValidationError(EMPTY_NAME_MESSAGE);
      return;
    }
    onSave(nome);
  };

  return (
    <Modal open={open} onClose={onClose} className={classes.modal}>
      <div className={classes.modalContainer}>
        <Typography className={classes.modalTitle}>{title}</Typography>
        {isSelect ? (
          <div className={classes.selectWrapper}>
            <Typography>{description}</Typography>
            <div className={classes.selectField}>
              <Select
                variant="outlined"
                displayEmpty
                error={Boolean(validationError)}
                value={inputValue || ''}
                onChange={handleInputChange}
                className={classes.select}
                style={{ paddingTop: 0 }}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <span className={classes.selectPlaceholder}>
                        {selectPlaceholder}
                      </span>
                    );
                  }
                  const tipologia = selectOption.find(
                    (item) => item.id === selected,
                  );
                  return tipologia ? labelTipologia(tipologia.nome) : selected;
                }}
              >
                {selectOption.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {labelTipologia(item.nome)}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText
                error
                role="alert"
                className={classes.fieldHelper}
              >
                {validationError || ' '}
              </FormHelperText>
            </div>
            <div className={classes.feedbackArea}>
              {loading && (
                <div className={classes.loadingWrapper}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="textSecondary">
                    Verifica associazioni in corso...
                  </Typography>
                </div>
              )}
              {warningMessage && (
                <Typography className={classes.warningText}>
                  {warningMessage}
                </Typography>
              )}
            </div>
          </div>
        ) : (
          <div className={classes.inputWrapper}>
            <Typography className={classes.inputDescription}>
              {description}
            </Typography>
            <TextField
              className={classes.nameField}
              value={inputValue}
              onChange={handleInputChange}
              variant="outlined"
              size="small"
              error={Boolean(validationError)}
              helperText={validationError || ' '}
              FormHelperTextProps={{
                role: 'alert',
                className: classes.fieldHelper,
              }}
            />
          </div>
        )}

        <div className={classes.buttonContainer}>
          <ActionButton
            secondaryButton
            disabled={false}
            grayVersion={false}
            label="Chiudi"
            onClick={onClose}
          />
          <ActionButton
            disabled={loading}
            grayVersion={false}
            label="Salva"
            onClick={handleSave}
          />
        </div>
      </div>
    </Modal>
  );
};

TipologiaStrumentazioneModal.propTypes = {
  description: PropTypes.string,
  inputValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onClose: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  title: PropTypes.string,
  selectOption: PropTypes.array,
  isSelect: PropTypes.bool,
  warningMessage: PropTypes.string,
  loading: PropTypes.bool,
  selectPlaceholder: PropTypes.string,
};

export default TipologiaStrumentazioneModal;
